<template>
    <DropdownMenuRoot v-model:open="open">
        <DropdownMenuTrigger :as-child="asChild">
            <slot name="trigger" />
        </DropdownMenuTrigger>
        <DropdownMenuPortal :disabled="disablePortal">
            <DropdownMenuContent
                :class="[classes.content, contentClass]"
                :side="side"
                :side-offset="sideOffset"
                :align="align"
                :collision-padding="collisionPadding"
                :aria-label="ariaLabel"
                @close-auto-focus="emit('closeAutoFocus', $event)">
                <DropdownMenuLabel v-if="label" :class="classes.label">{{ label }}</DropdownMenuLabel>
                <template v-for="(section, sectionIndex) in renderedSections" :key="section.id">
                    <DropdownMenuSeparator v-if="sectionIndex > 0" :class="classes.separator" />
                    <RekaDropdownMenuItem
                        v-for="item in section.items"
                        :key="item.id"
                        :ref="(instance) => markUnavailable(instance, item)"
                        :as="itemTag(item)"
                        :type="item.type === 'button' ? 'button' : undefined"
                        :href="item.type === 'button' ? undefined : item.href"
                        :disabled="item.disabled"
                        :text-value="item.label"
                        :aria-current="item.ariaCurrent || undefined"
                        :data-testid="item.testId"
                        :class="[classes.item, classes[item.theme ?? 'default'], item.itemClass]"
                        @select="onSelect(item, $event)">
                        <slot :name="`item-${item.id}`" :item="item">
                            <span v-if="item.icon && typeof item.icon !== 'string'" class="h-4 w-4 shrink-0" aria-hidden="true">
                                <component :is="item.icon" />
                            </span>
                            <i v-else-if="item.icon" class="shrink-0 text-base leading-none" :class="item.icon" aria-hidden="true"></i>
                            {{ item.label }}
                        </slot>
                    </RekaDropdownMenuItem>
                </template>
            </DropdownMenuContent>
        </DropdownMenuPortal>
    </DropdownMenuRoot>
</template>

<script lang="ts">
import type { Component, ComponentPublicInstance } from "vue";

type DropdownMenuItemBase = {
    // IDs are used for keys and item-specific slots (`item-${id}`), so they must be unique within a menu.
    id: string;
    label: string;
    icon?: string | Component;
    disabled?: boolean;
    // Unavailable but still focusable, for items whose label explains why (e.g. "assign another lead first").
    // A `disabled` item is skipped by keyboard navigation, so its explanation would be unreachable.
    ariaDisabled?: boolean;
    theme?: "default" | "danger";
    ariaCurrent?: boolean;
    testId?: string;
    itemClass?: string;
};

// Submenus are intentionally unsupported until a concrete product use case requires DropdownMenuSub.
export type DropdownMenuItem =
    | (DropdownMenuItemBase & { type: "button" })
    | (DropdownMenuItemBase & { type: "anchor"; href: string })
    | (DropdownMenuItemBase & { type: "inertia-link"; href: string });

// multiple sections are separated by a Separator component
export type DropdownMenuSection = {
    id: string;
    items: DropdownMenuItem[];
};
</script>

<script setup lang="ts">
import BaseLink from "~vue/router/BaseLink.vue";
import { invariant } from "~vue/utils";
import {
    DropdownMenuContent,
    type DropdownMenuContentProps,
    DropdownMenuItem as RekaDropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "reka-ui";
import { computed, watchEffect } from "vue";

type BaseDropdownMenuProps = Partial<Pick<DropdownMenuContentProps, "align" | "collisionPadding" | "side" | "sideOffset">> & {
    sections: DropdownMenuSection[];
    // Merges the trigger onto the slotted element, so the trigger is not a button wrapping a button.
    asChild?: boolean;
    // Accessible name for the menu itself; only needed when the trigger's name doesn't describe it.
    ariaLabel?: string;
    // Heading rendered above the items, for menus whose trigger label is not enough context.
    label?: string;
    contentClass?: string;
    // Renders the content inline; mostly useful for testing.
    disablePortal?: boolean;
};

const {
    sections,
    asChild = true,
    side = "bottom",
    sideOffset = 4,
    align = "end",
    collisionPadding = 8,
    contentClass = "",
    disablePortal = false,
} = defineProps<BaseDropdownMenuProps>();

const open = defineModel<boolean>("open");
const emit = defineEmits<{
    select: [item: DropdownMenuItem];
    // Reka returns focus to the trigger when the menu closes; consumers that need
    // focus elsewhere (e.g. an inline edit field) call preventDefault on this event.
    closeAutoFocus: [event: Event];
}>();

watchEffect(() => {
    const itemIds = sections.flatMap((section) => section.items.map((item) => item.id));
    invariant(new Set(itemIds).size === itemIds.length, "BaseDropdownMenu item IDs must be unique within a menu");
});

const renderedSections = computed(() => sections.filter((section) => section.items.length > 0));

// reka writes aria-disabled from its own `disabled` prop after our attrs, so it can't be bound; set it on the
// rendered element instead. reka's value stays undefined across re-renders, so Vue never patches it away.
function markUnavailable(instance: unknown, item: DropdownMenuItem) {
    const element = (instance as ComponentPublicInstance | null)?.$el;
    if (item.ariaDisabled && element instanceof HTMLElement) {
        element.setAttribute("aria-disabled", "true");
    }
}

function onSelect(item: DropdownMenuItem, event: Event) {
    if (item.ariaDisabled) {
        // Keeps the menu open and the item focused, as choosing an unavailable action should do nothing.
        event.preventDefault();
        return;
    }
    emit("select", item);
}

const itemTag = (item: DropdownMenuItem) => {
    if (item.type === "anchor") {
        return "a";
    }
    return item.type === "inertia-link" ? BaseLink : "button";
};
</script>

<style module="classes">
@reference "../../../css/tailwind.css";

@layer components {
    .content {
        @apply flex flex-col min-w-60 p-2 rounded-xl border border-dropdown-menu-border bg-surface-raised shadow-elevated z-40 focus-visible:outline-0;
    }

    .label {
        @apply body-small text-ink-muted uppercase px-2 pb-1;
    }

    .item {
        @apply flex items-center gap-3 w-full px-3 py-2.5 rounded-lg body-small text-left text-dropdown-menu-item-text whitespace-nowrap cursor-pointer select-none;
        @apply data-highlighted:outline-0 data-disabled:cursor-default data-disabled:text-ink-muted aria-disabled:cursor-default aria-disabled:text-ink-muted;
    }

    .default {
        @apply data-highlighted:bg-dropdown-menu-item-hover;
    }

    .danger {
        @apply text-dropdown-menu-danger-text data-highlighted:bg-dropdown-menu-danger-hover;
    }

    .separator {
        @apply my-1 border-t border-dropdown-menu-separator;
    }
}
</style>
